/* eslint-disable guard-for-in */
import { day, night } from '@platform-ui/themes';
import { POLL_THEME_MAPPING, ThemeFontFamilies } from '../kinds/poll/theme';
import { WEBVIEW_THEME_MAPPING } from '../kinds/webview/theme';
import { TrueTheme } from '../types/poll.generated';
import { DIALOG_THEME_VARIABLES, POLL_THEME_VARIABLES, WEBVIEW_THEME_VARIABLES } from './themeVariables';

export function createDefaultTheme(): TrueTheme['data'] {
  return {
    poll: {
      colors: getDefaultPollColors(),
      fonts: getDefaultFonts(),
      others: getDefaultOthers(),
      forceColorScheme: undefined,
      logotype: undefined,
    },
    dialog: {
      colors: getDefaultDialogColors(),
      forceColorScheme: undefined,
    },
    webview: {
      colors: getDefaultWebviewColors(),
      others: getDefaultWebviewOthers(),
    },
  };
}

export const getDefaultPollColors = () => {
  const colors = {} as Record<keyof typeof POLL_THEME_VARIABLES.colors, { light: string; dark: string }>;

  for (const key in POLL_THEME_VARIABLES.colors) {
    const abstractPropertyName = key as keyof typeof POLL_THEME_VARIABLES.colors;
    const tuiCssName = POLL_THEME_MAPPING.colors[abstractPropertyName];

    const dayValue: string = Array.isArray(tuiCssName)
      ? day[tuiCssName[0] as keyof typeof day]
      : day[tuiCssName as keyof typeof day];

    const nightValue: string = Array.isArray(tuiCssName)
      ? night[tuiCssName[0] as keyof typeof night]
      : night[tuiCssName as keyof typeof night];

    colors[abstractPropertyName] = {
      light: dayValue || '',
      dark: nightValue || '',
    };
  }

  //Обрабатываем кастомные значения, т.к. --custom переменных нет в day и night
  colors['progress_bar_line_filled'] = {
    dark: night['--tui-background-neutral-4'],
    light: day['--tui-background-neutral-4'],
  };

  colors['progress_bar_line'] = {
    dark: night['--tui-background-neutral-3'],
    light: day['--tui-background-neutral-3'],
  };

  return colors;
};

export const getDefaultDialogColors = () => {
  const colors = {} as Record<keyof typeof DIALOG_THEME_VARIABLES.colors, { light: string; dark: string }>;

  for (const key in DIALOG_THEME_VARIABLES.colors) {
    const abstractPropertyName = key as keyof typeof DIALOG_THEME_VARIABLES.colors;
    const tuiCssName = POLL_THEME_MAPPING.colors[abstractPropertyName];

    const dayValue: string = Array.isArray(tuiCssName)
      ? day[tuiCssName[0] as keyof typeof day]
      : day[tuiCssName as keyof typeof day];

    const nightValue: string = Array.isArray(tuiCssName)
      ? night[tuiCssName[0] as keyof typeof night]
      : night[tuiCssName as keyof typeof night];

    colors[abstractPropertyName] = {
      light: dayValue || '',
      dark: nightValue || '',
    };
  }

  //Обрабатываем кастомные значения, т.к. --custom переменных нет в day и night
  colors['progress_bar_line_filled'] = {
    dark: night['--tui-background-accent'],
    light: day['--tui-background-accent'],
  };

  colors['progress_bar_line'] = {
    dark: night['--tui-background-neutral-3'],
    light: day['--tui-background-neutral-3'],
  };

  return colors;
};

export const getDefaultWebviewColors = () => {
  const colors = {} as Record<keyof typeof WEBVIEW_THEME_VARIABLES.colors, { light: string; dark: string }>;

  for (const key in WEBVIEW_THEME_VARIABLES.colors) {
    const abstractPropertyName = key as keyof typeof WEBVIEW_THEME_VARIABLES.colors;
    const cssName = POLL_THEME_MAPPING.colors[abstractPropertyName];

    const dayValue: string = Array.isArray(cssName)
      ? day[cssName[0] as keyof typeof day]
      : day[cssName as keyof typeof day];

    const nightValue: string = Array.isArray(cssName)
      ? night[cssName[0] as keyof typeof night]
      : night[cssName as keyof typeof night];

    colors[abstractPropertyName] = {
      light: dayValue || '',
      dark: nightValue || '',
    };
  }

  //Обрабатываем кастомные значения, т.к. --custom переменных нет в day и night
  colors['progress_bar_line_filled'] = {
    dark: night['--tui-background-accent'],
    light: day['--tui-background-accent'],
  };

  colors['progress_bar_line'] = {
    dark: night['--tui-background-neutral-3'],
    light: day['--tui-background-neutral-3'],
  };

  //background_neutral_2 - отвечает за бэкграунд ответов
  //ниже скидываем цвета до прозрачного цвета чтобы не было бэкграунда при создании темы
  colors['background_neutral_2'] = {
    dark: `rgba(255, 255, 255, 0)`,
    light: `rgba(255, 255, 255, 0)`,
  };

  return colors;
};

export const getDefaultFonts = () => {
  return {
    font_family: {
      heading_font_family: 'dsHeading' as ThemeFontFamilies,
      text_font_family: 'dsText' as ThemeFontFamilies,
    },
    font_variables: {
      font_heading_survey_title: {
        desktop: {
          fontWeight: '500',
          fontSize: '36',
          lineHeight: '40',
        },
        mobile: {
          fontWeight: '700',
          fontSize: '20',
          lineHeight: '23',
        },
      },
      font_heading_page_title: {
        desktop: { fontWeight: '500', fontSize: '24', lineHeight: '28' },
        mobile: { fontWeight: '700', fontSize: '28', lineHeight: '33' },
      },
      font_text_body: {
        desktop: { fontWeight: '500', fontSize: '19', lineHeight: '28' },
        mobile: { fontWeight: '500', fontSize: '16', lineHeight: '19' },
      },
      font_text_main: {
        desktop: { fontWeight: '400', fontSize: '15', lineHeight: '24' },
        mobile: { fontWeight: '400', fontSize: '14', lineHeight: '16' },
      },
      font_text_hints: {
        desktop: { fontWeight: '400', fontSize: '13', lineHeight: '20' },
        mobile: { fontWeight: '400', fontSize: '13', lineHeight: '15' },
      },
      font_text_micro_hints: {
        desktop: { fontWeight: '400', fontSize: '11', lineHeight: '16' },
        mobile: { fontWeight: '400', fontSize: '12', lineHeight: '14' },
      },
      font_text_main_ui: {
        desktop: { fontWeight: '400', fontSize: '15', lineHeight: '20' },
        mobile: { fontWeight: '400', fontSize: '14', lineHeight: '16' },
      },
    },
  };
};

export const getDefaultOthers = () => {
  return {
    survey_elements_border_radius: POLL_THEME_MAPPING.custom['--custom-survey-elements-border-radius'] ?? '',
    survey_container_border_radius_desktop:
      POLL_THEME_MAPPING.custom['--custom-survey-container-border-radius-desktop'] ?? '',
    survey_container_border_radius_mobile:
      POLL_THEME_MAPPING.custom['--custom-survey-container-border-radius-mobile'] ?? '',
    survey_container_inner_padding_desktop:
      POLL_THEME_MAPPING.custom['--custom-survey-container-inner-padding-desktop'] ?? '',
    survey_container_inner_padding_mobile:
      POLL_THEME_MAPPING.custom['--custom-survey-container-inner-padding-mobile'] ?? '',
  };
};

export const getDefaultWebviewOthers = () => {
  return {
    webview_elements_border_radius: WEBVIEW_THEME_MAPPING.custom['--custom-survey-elements-border-radius'] ?? '',
    webview_answer_inner_padding: WEBVIEW_THEME_MAPPING.custom['--custom-webview-answer-inner-padding'] ?? '',
    webview_answer_border_radius: WEBVIEW_THEME_MAPPING.custom['--custom-webview-answer-border-radius'] ?? '',
    webview_footer_button_position: WEBVIEW_THEME_MAPPING.custom['--custom-webview-footer-button-position'] ?? 'normal',
  };
};
